import {
  DefaultButton,
  DialogFooter,
  IconButton,
  Label,
  mergeStyleSets,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import ErrorMsg from "components/controls/ErrorMsg";
import NumberInputField from "components/controls/NumberInputField";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";

// One deductible option as edited: the percent as a decimal string
// (DoubleNumberInputField, e.g. "2,5"), the amounts (CZK) as digit strings
// (NumberInputField); "" = not defined.
export interface DeductibleOptionRow {
  percent: string;
  min: string;
  max: string;
}

export const EMPTY_DEDUCTIBLE_OPTION_ROW: DeductibleOptionRow = {
  percent: "",
  min: "",
  max: "",
};

// Formik field (and flat error key) of an option's part.
export const deductibleOptionFieldName = (
  index: number,
  field: keyof DeductibleOptionRow,
) => `options.${index}.${field}`;

const HullDeductibleOptionsFormFields = ({
  fieldProps,
  insurerOptions,
  isLoading,
}) => {
  const classes = getClassNames();
  const { t } = useTranslation();
  const { setFieldValue, values, errors, handleChange } = fieldProps;
  const isReadOnly =
    useSelector((s) => s.fleetForm.dialog.type) === EActionDialog.ReadOnly;

  const options: DeductibleOptionRow[] = values.options || [];

  const addOption = () =>
    setFieldValue("options", [...options, { ...EMPTY_DEDUCTIBLE_OPTION_ROW }]);

  const removeOption = (index: number) =>
    setFieldValue(
      "options",
      options.filter((_, i) => i !== index),
    );

  return (
    <>
      <div className={classes.row}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.validFromDate}
            name={"validFromDate"}
            disabled={isReadOnly}
            label={"bfm.vehicleForm.validFromDate.label"}
            placeholder={t("bfm.selectDate")}
            required
            confidentiality={null}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <ComboBoxField
            id="insurerPartnerNumber"
            disabled={isReadOnly}
            errors={errors}
            required
            name="insurerPartnerNumber"
            label={"bfm.insurer.label"}
            setFieldValue={setFieldValue}
            options={insurerOptions || []}
            defaultValue={values?.insurerPartnerNumber}
            confidentiality={null}
          />
        </div>
      </div>

      <Label required className={classes.optionsLabel}>
        {t("bfm.hullDeductibleOptions.label")}
      </Label>
      {options.map((row, index) => (
        <div key={index} className={classes.row}>
          <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
            <DoubleNumberInputField
              setFieldValue={setFieldValue}
              value={row.percent}
              onChange={handleChange}
              errors={errors}
              name={deductibleOptionFieldName(index, "percent")}
              disabled={isReadOnly}
              label={"bfm.hullDeductibleOptions.percent.label"}
            />
          </div>
          <div
            className={classes.fieldItem}
            style={{ marginLeft: "10px", marginRight: "10px" }}
          >
            <NumberInputField
              setFieldValue={setFieldValue}
              value={row.min}
              errors={errors}
              name={deductibleOptionFieldName(index, "min")}
              disabled={isReadOnly}
              label={"bfm.hullDeductibleOptions.min.label"}
            />
          </div>
          <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
            <NumberInputField
              setFieldValue={setFieldValue}
              value={row.max}
              errors={errors}
              name={deductibleOptionFieldName(index, "max")}
              disabled={isReadOnly}
              label={"bfm.hullDeductibleOptions.max.label"}
            />
          </div>
          <div className={classes.removeButton}>
            {/* at least one option */}
            {!isReadOnly && options.length > 1 && (
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                title={t("greco.delete")}
                ariaLabel={t("greco.delete")}
                onClick={() => removeOption(index)}
              />
            )}
          </div>
        </div>
      ))}
      {errors.options && <ErrorMsg>{errors.options}</ErrorMsg>}
      {!isReadOnly && (
        <DefaultButton
          className={classes.addButton}
          text={t("bfm.hullDeductibleOptions.addOption.label")}
          iconProps={{ iconName: "Add" }}
          onClick={addOption}
        />
      )}

      <div className={classes.placeholder}></div>
      {!isReadOnly && (
        <DialogFooter className={classes.footer}>
          <DefaultButton
            id="saveButton"
            primary
            text={t("greco.save")}
            type="submit"
            iconProps={{
              iconName: "Save",
            }}
            disabled={isLoading}
          />
        </DialogFooter>
      )}
    </>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
    },
    fieldItem: {
      width: "100%",
      marginTop: 10,
    },
    optionsLabel: {
      marginTop: 20,
    },
    removeButton: {
      width: 40,
      flexShrink: 0,
      paddingTop: 38,
    },
    addButton: {
      marginTop: 15,
    },
    placeholder: {
      height: 100,
    },
    footer: {
      marginTop: 50,
    },
  });

export default HullDeductibleOptionsFormFields;
